/* eslint-disable no-param-reassign */
import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { SrtStream } from '../../../stores/reducers/video';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import useAudioControls from './use-audio-controls';
import useDragItem from '../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import { GreenroomState as GS } from '../../../stores/types/types';
import { ThumbnailLocation } from '../../../types/enums';
import RightClickMenu from '../../general-components/right-click-menu';
import { setSelectedSrtStreamId } from '../../../stores/actions/video';
import { setMetaMenuRoute, setMetaMenuState } from '../../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../../stores/types/tabs';
import { setOpenContextMenuId } from '../../../stores/actions/user-interface';
import useShots from '../../../contexts/shots/provider';
import { FEED_TYPES } from '../../../stores/reducers/shots';
import DefaultSafetyImage from '../../../images/default-safety-image.png';

interface Props {
	srtStream: SrtStream;
	location: ThumbnailLocation;
}

export default function SrtStreamThumbnail({ srtStream, location }: Props) {
	const {
		id,
		thumbnail,
		name,
		safetyImage,
	} = srtStream;

	const {
		FeedsTab,
		ShotModal,
	} = ThumbnailLocation;

	const dispatch = useDispatch();

	const host = useSelector((s: GS) => s.SessionReducer.host);

	const [rightClickCoords, setRightClickCoords] = useState<[number, number]>();

	const {
		shotModalShot: {
			shot_data: {
				shotFeeds: modalShotFeeds,
			},
		},
	} = useSelector((s: GS) => s.ShotsReducer);

	const {
		currentShot: {
			shot_data: {
				shotFeeds: currentShotFeeds,
			},
		},
	} = useShots();

	const canDrag = useMemo(
		() => host && (location === FeedsTab || location === ShotModal),
		[host, location],
	);

	const feeds = useMemo(
		() => (location === ThumbnailLocation.ShotModal ? modalShotFeeds : currentShotFeeds),
		[location, modalShotFeeds, currentShotFeeds],
	);

	const inShot = useMemo(
		() => feeds.some((f) => f.feedType === FEED_TYPES.SRT_STREAM && f.srtStreamId === id),
		[feeds],
	);

	const {
		localVolume,
		localMuted,
		handleMuteChange,
		handleVolumeChange,
	} = useAudioControls(srtStream);

	const {
		dragRef,
		isDragging,
	} = useDragItem({ id, type: DRAG_TYPE.FEED_SRT_STREAM }, canDrag);

	const handleEditSrtFeed = () => {
		dispatch(setSelectedSrtStreamId(id));
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.SRT));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
	};

	return (
		<>
			{isDragging && <div className="video-thumb-drag-placeholder" />}
			<RightClickMenu
				contextMenuId={`video-feed-${srtStream.id}`}
				items={[{
					text: 'Edit SRT Feed',
					handleClick: handleEditSrtFeed,
				}]}
				clickCoordinates={rightClickCoords}
			/>
			<div
				ref={dragRef}
				className={classNames('video-thumb', {
					'is-dragging': isDragging,
					'cursor-grab': canDrag,
				})}
				onContextMenu={(e) => {
					if (canDrag) {
						e.preventDefault();
						setRightClickCoords([e.pageX, e.pageY]);
						dispatch(setOpenContextMenuId(`video-feed-${srtStream.id}`));
					}
				}}
			>
				<div className="video-wrapper">
					<div className="video-overlay flex-center">
						<div className="poster">
							<img
								className="poster-image"
								src={thumbnail || safetyImage}
								alt={name}
								onError={({ currentTarget }) => {
									currentTarget.src = DefaultSafetyImage;
								}}
							/>
						</div>
						<div className="video-overlay-bottom">
							<label className="bl-font-l1">{name}</label>
						</div>
						{location === ThumbnailLocation.ShotTab && (
							<div
								className="flex-center flex-column width-full padding-button-small background-transparent"
								style={{ background: 'rgba(0, 0, 0, 0.8)' }}
							>
								<div className="align-items-center position-relative vol-wrap">
									<div
										className={classNames('flex-center', { localMuted })}
										onClick={handleMuteChange}
									>
										<Icon
											name={localMuted ? ICONS.AUDIO_MUTED : ICONS.SOUND}
											size={18}
											color={localMuted ? COLORS.GRAY : COLORS.WHITE}
										/>
									</div>
									<input
										type="range"
										className="volume"
										onChange={handleVolumeChange}
										value={localMuted ? 0 : localVolume}
										step={0.01}
										min={0}
										max={1}
										style={{ background: 'white', marginLeft: '8px' }}
									/>
								</div>
							</div>
						)}
					</div>
					<div className={classNames('selection-border', inShot && 'in-shot')} />
				</div>
			</div>
		</>
	);
}
